import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUniverseTheme } from '../../contexts/UniverseThemeContext';
import importantService from '../../services/importantService';
import rpActivityService from '../../services/rpActivityService';
import universeService from '../../services/universeService';
import { getUniverseForumsPath, getElseworldForumsPath } from '../../utils/universePaths';
import { canUseAiScenes } from '../../utils/staffPermissions';
import styles from './UniverseSidebar.module.css';

const ICONS_BY_KEY = {
  reglement: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 3h9l3 3v15H6z" />
      <path d="M14 3v4h4" />
      <path d="M9 11h6M9 15h6" />
    </svg>
  ),
  mode_emploi: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 2-2.5 2-2.5 4" />
      <path d="M12 17h.01" />
    </svg>
  ),
  member_of_month: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7L6.8 19l1-5.8-4.2-4.1 5.8-.8z" />
    </svg>
  ),
  character_of_month: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l2 4 4 .6-3 2.9.8 4.1L12 12.7 8.2 14.6l.8-4.1-3-2.9 4-.6z" />
      <path d="M5 21a7 7 0 0 1 14 0" />
    </svg>
  ),
  rp_activities: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v4H4z" />
      <path d="M4 11h10v8H4z" />
      <path d="M16 12h4v7h-4z" />
    </svg>
  ),
  vote: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 11l4 4L19 5" />
      <path d="M4 14v5h16v-7" />
      <path d="M8 19v2h8v-2" />
    </svg>
  ),
  features: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h7v7h-7z" />
    </svg>
  ),
  factions: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l8 4v6c0 4.5-3.5 9-8 10-4.5-1-8-5.5-8-10V7l8-4z" />
    </svg>
  ),
  animator_dashboard: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 19h16" />
      <path d="M7 16V9" />
      <path d="M12 16V5" />
      <path d="M17 16v-4" />
      <circle cx="12" cy="3" r="1.5" />
    </svg>
  ),
  dashboard: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" rx="1" />
      <rect x="13" y="3" width="8" height="5" rx="1" />
      <rect x="13" y="11" width="8" height="10" rx="1" />
      <rect x="3" y="14" width="8" height="7" rx="1" />
    </svg>
  ),
  my_characters: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="9" cy="7" r="3.5" />
      <path d="M4 20v-1a4 4 0 0 1 4-4h2a4 4 0 0 1 4 4v1" />
      <circle cx="17" cy="8" r="2.5" />
      <path d="M15 20v-0.5a3 3 0 0 1 3-3h1" />
    </svg>
  ),
  my_factions: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 3h12v18l-6-4-6 4V3z" />
    </svg>
  ),
  forums: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 6h16v12H4z" />
      <path d="M8 10h8M8 14h5" />
    </svg>
  ),
  about: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 10v6M12 7h.01" />
    </svg>
  ),
};

const SIDEBAR_PARTITION_KEYS = new Set([
  'reglement',
  'mode_emploi',
  'vote',
  'features',
  'rp_activities',
  'member_of_month',
  'character_of_month',
]);

const ESSENTIAL_LINKS = [
  { key: 'forums', label: 'Forums' },
  { key: 'about', url: '/qui-sommes-nous', label: 'Qui sommes-nous' },
];

const FEATURES_LINK = {
  key: 'features',
  url: '/fonctionnalites',
  label: 'Fonctionnalités',
};

const FACTIONS_LINK = {
  key: 'factions',
  url: '/factions',
  label: 'Factions',
};

const ANIMATOR_DASHBOARD_LINK = {
  key: 'animator_dashboard',
  url: '/espace-animateur',
  label: 'Espace animateur',
};

const ACCOUNT_LINKS = [
  { key: 'dashboard', url: '/tableau-de-bord', label: 'Mon tableau de bord' },
  { key: 'my_characters', url: '/characters', label: 'Mes personnages' },
  { key: 'my_factions', url: '/mes-factions', label: 'Mes factions' },
];

const readSectionOpen = (key, defaultValue) => {
  if (typeof window === 'undefined') {
    return defaultValue;
  }
  const stored = localStorage.getItem(`sidebar-section-${key}`);
  if (stored === '0') {
    return false;
  }
  if (stored === '1') {
    return true;
  }
  return defaultValue;
};

const CollapsibleSection = ({
  title,
  sectionId,
  isOpen,
  onToggle,
  effectiveCollapsed,
  hasUnread,
  children,
}) => {
  const showChildren = effectiveCollapsed || isOpen;

  return (
    <section className={styles.category} aria-label={title}>
      {!effectiveCollapsed && (
        <button
          type="button"
          className={styles.sectionToggle}
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={`sidebar-section-${sectionId}`}
        >
          <span className={styles.sectionToggleLabel}>{title}</span>
          {hasUnread && <span className={styles.sectionUnreadDot} aria-hidden="true" />}
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className={`${styles.sectionChevron} ${isOpen ? styles.sectionChevronOpen : ''}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      )}
      {showChildren && (
        <div
          id={`sidebar-section-${sectionId}`}
          className={styles.categoryLinks}
        >
          {children}
        </div>
      )}
    </section>
  );
};

const UniverseSidebar = ({
  collapsed = false,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
}) => {
  const location = useLocation();
  const { user } = useAuth();
  const { currentUniverse, currentElseworld } = useUniverseTheme();
  const [items, setItems] = useState([]);
  const [currentElseworldName, setCurrentElseworldName] = useState('');
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [memberOfMonthUnread, setMemberOfMonthUnread] = useState(false);
  const [characterOfMonthUnread, setCharacterOfMonthUnread] = useState(false);
  const [rpActivitiesUnread, setRpActivitiesUnread] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(() => readSectionOpen('community', true));
  const [resourcesOpen, setResourcesOpen] = useState(() => readSectionOpen('resources', false));
  const [accountOpen, setAccountOpen] = useState(() => readSectionOpen('account', true));

  const universeSlug = useMemo(() => (currentUniverse !== 'portal' ? currentUniverse : ''), [currentUniverse]);
  const forumsPath = useMemo(() => {
    if (currentElseworld && universeSlug) {
      return getElseworldForumsPath(universeSlug, currentElseworld);
    }
    return getUniverseForumsPath(universeSlug);
  }, [universeSlug, currentElseworld]);

  const itemsByKey = useMemo(() => {
    const map = new Map();
    items.forEach((item) => map.set(item.key, item));
    return map;
  }, [items]);

  const guidanceItems = useMemo(() => {
    const fromApi = ['reglement', 'mode_emploi', 'vote']
      .map((key) => itemsByKey.get(key))
      .filter(Boolean);
    fromApi.push(itemsByKey.get('features') || FEATURES_LINK);
    return fromApi;
  }, [itemsByKey]);

  const forumOrphanItems = useMemo(
    () => items.filter((item) => !SIDEBAR_PARTITION_KEYS.has(item.key)),
    [items],
  );

  const showAnimatorDashboard = Boolean(user && canUseAiScenes(user));

  const hasCommunitySection = useMemo(
    () => Boolean(
      itemsByKey.get('rp_activities')
      || itemsByKey.get('member_of_month')
      || itemsByKey.get('character_of_month')
      || forumOrphanItems.length > 0
      || FACTIONS_LINK
      || showAnimatorDashboard,
    ),
    [itemsByKey, forumOrphanItems, showAnimatorDashboard],
  );

  const communityHasUnread = memberOfMonthUnread || characterOfMonthUnread || rpActivitiesUnread;

  const persistSectionOpen = useCallback((key, value) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`sidebar-section-${key}`, value ? '1' : '0');
    }
  }, []);

  const toggleCommunity = useCallback(() => {
    setCommunityOpen((prev) => {
      const next = !prev;
      persistSectionOpen('community', next);
      return next;
    });
  }, [persistSectionOpen]);

  const toggleResources = useCallback(() => {
    setResourcesOpen((prev) => {
      const next = !prev;
      persistSectionOpen('resources', next);
      return next;
    });
  }, [persistSectionOpen]);

  const toggleAccount = useCallback(() => {
    setAccountOpen((prev) => {
      const next = !prev;
      persistSectionOpen('account', next);
      return next;
    });
  }, [persistSectionOpen]);

  useEffect(() => {
    const loadSidebar = async () => {
      try {
        const data = await importantService.getSidebar(universeSlug);
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (error) {
        setItems([]);
      }
    };

    loadSidebar();
  }, [universeSlug]);

  useEffect(() => {
    const loadElseworldName = async () => {
      if (!universeSlug || !currentElseworld) {
        setCurrentElseworldName('');
        return;
      }

      try {
        const elseworlds = await universeService.getUniverseElseworlds(universeSlug);
        const match = elseworlds.find((item) => item.slug === currentElseworld);
        setCurrentElseworldName(match?.name || currentElseworld);
      } catch (error) {
        setCurrentElseworldName(currentElseworld);
      }
    };

    loadElseworldName();
  }, [universeSlug, currentElseworld]);

  useEffect(() => {
    onMobileClose?.();
  }, [location.pathname, onMobileClose]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const syncViewport = () => setIsMobileViewport(mediaQuery.matches);

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);

    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  const effectiveCollapsed = isMobileViewport ? false : collapsed;

  useEffect(() => {
    const syncMemberOfMonthUnread = async () => {
      if (!universeSlug) {
        setMemberOfMonthUnread(false);
        return;
      }

      try {
        const onMemberPage = location.pathname.startsWith('/member-of-month');
        if (onMemberPage) {
          await importantService.markMemberOfMonthSeen(universeSlug);
          setMemberOfMonthUnread(false);
          return;
        }

        const response = await importantService.getMemberOfMonth(universeSlug);
        setMemberOfMonthUnread(Boolean(response?.isUnread));
      } catch (error) {
        setMemberOfMonthUnread(false);
      }
    };

    syncMemberOfMonthUnread();
  }, [location.pathname, universeSlug]);

  useEffect(() => {
    const syncCharacterOfMonthUnread = async () => {
      if (!universeSlug) {
        setCharacterOfMonthUnread(false);
        return;
      }

      try {
        const onCharacterPage = location.pathname.startsWith('/character-of-month');
        if (onCharacterPage) {
          await importantService.markCharacterOfMonthSeen(universeSlug);
          setCharacterOfMonthUnread(false);
          return;
        }

        const response = await importantService.getCharacterOfMonth(universeSlug);
        setCharacterOfMonthUnread(Boolean(response?.isUnread));
      } catch (error) {
        setCharacterOfMonthUnread(false);
      }
    };

    syncCharacterOfMonthUnread();
  }, [location.pathname, universeSlug]);

  useEffect(() => {
    const syncRpActivitiesUnread = async () => {
      if (!universeSlug) {
        setRpActivitiesUnread(false);
        return;
      }

      try {
        const onActivitiesPage = location.pathname.startsWith('/rp-activities');
        if (onActivitiesPage) {
          await rpActivityService.markUniverseActivitiesSeen(universeSlug);
          setRpActivitiesUnread(false);
          return;
        }

        const response = await rpActivityService.getUniverseActivities(universeSlug);
        const hasUnread = Boolean(response?.hasUnread)
          || (Array.isArray(response?.items) && response.items.some((item) => Boolean(item?.isUnread)));
        setRpActivitiesUnread(hasUnread);
      } catch (error) {
        setRpActivitiesUnread(false);
      }
    };

    syncRpActivitiesUnread();
  }, [location.pathname, universeSlug]);

  const isGuidancePathActive = (item) => {
    if (!item?.url) {
      return false;
    }
    return location.pathname === item.url || location.pathname.startsWith(`${item.url}/`);
  };

  useEffect(() => {
    if (location.pathname.startsWith('/espace-animateur')) {
      setCommunityOpen(true);
      persistSectionOpen('community', true);
    }
  }, [location.pathname, persistSectionOpen]);

  useEffect(() => {
    const guidanceActive = guidanceItems.some((item) => isGuidancePathActive(item));
    if (guidanceActive) {
      setResourcesOpen(true);
      persistSectionOpen('resources', true);
    }
  }, [location.pathname, guidanceItems, persistSectionOpen]);

  useEffect(() => {
    const accountActive = ACCOUNT_LINKS.some((item) => {
      switch (item.key) {
        case 'dashboard':
          return location.pathname.startsWith('/tableau-de-bord');
        case 'my_characters':
          return location.pathname.startsWith('/characters');
        case 'my_factions':
          return location.pathname.startsWith('/mes-factions');
        default:
          return false;
      }
    });
    if (accountActive) {
      setAccountOpen(true);
      persistSectionOpen('account', true);
    }
  }, [location.pathname, persistSectionOpen]);

  const isLinkActive = (item) => {
    switch (item.key) {
      case 'forums':
        return location.pathname === '/forums'
          || location.pathname.startsWith('/forums/')
          || (location.pathname.startsWith('/univers/') && !currentElseworld);
      case 'about':
        return location.pathname.startsWith('/qui-sommes-nous');
      case 'dashboard':
        return location.pathname.startsWith('/tableau-de-bord');
      case 'my_characters':
        return location.pathname.startsWith('/characters');
      case 'my_factions':
        return location.pathname.startsWith('/mes-factions');
      case 'factions':
        return location.pathname.startsWith('/factions');
      case 'animator_dashboard':
        return location.pathname.startsWith('/espace-animateur');
      case 'rp_activities':
        return location.pathname.startsWith('/rp-activities');
      case 'reglement':
        return location.pathname.startsWith('/reglement');
      case 'mode_emploi':
        return location.pathname.startsWith('/mode-emploi');
      case 'features':
        return location.pathname.startsWith('/fonctionnalites');
      case 'member_of_month':
        return location.pathname.startsWith('/member-of-month');
      case 'character_of_month':
        return location.pathname.startsWith('/character-of-month');
      default:
        return location.pathname === item.url;
    }
  };

  const resolveLinkUrl = (item) => {
    if (item.key === 'forums') {
      return forumsPath;
    }
    return item.url;
  };

  const renderLink = (item) => {
    const disabled = !universeSlug && (
      item.key === 'member_of_month'
      || item.key === 'character_of_month'
      || item.key === 'rp_activities'
    );
    const unread = (
      (item.key === 'member_of_month' && memberOfMonthUnread)
      || (item.key === 'character_of_month' && characterOfMonthUnread)
      || (item.key === 'rp_activities' && rpActivitiesUnread)
    ) && !disabled;
    const icon = ICONS_BY_KEY[item.key] || (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" />
        <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
      </svg>
    );

    const content = (
      <>
        <span className={styles.icon} aria-hidden="true">
          {icon}
          {unread && <span className={styles.unreadDot} />}
        </span>
        {!effectiveCollapsed && <span className={styles.label}>{item.label}</span>}
      </>
    );

    if (item.isExternal) {
      return (
        <a
          href={item.url}
          className={`${styles.link} ${unread ? styles.linkUnread : ''} ${disabled ? styles.linkDisabled : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          title={item.label}
          onClick={(event) => {
            if (disabled) {
              event.preventDefault();
            }
          }}
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        to={resolveLinkUrl(item)}
        className={`${styles.link} ${unread ? styles.linkUnread : ''} ${isLinkActive(item) ? styles.linkActive : ''} ${disabled ? styles.linkDisabled : ''}`}
        title={item.label}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
          }
        }}
      >
        {content}
      </Link>
    );
  };

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          className={styles.backdrop}
          aria-label="Fermer le menu de navigation"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''} ${mobileOpen ? styles.sidebarMobileOpen : ''}`}
        aria-hidden={isMobileViewport && !mobileOpen}
      >
        <div className={styles.header}>
          <button
            type="button"
            className={styles.collapseButton}
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Agrandir la sidebar' : 'Réduire la sidebar'}
            title={collapsed ? 'Agrandir' : 'Réduire'}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className={collapsed ? styles.chevronOpen : ''}>
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          {isMobileViewport && (
            <button
              type="button"
              className={styles.mobileCloseButton}
              onClick={onMobileClose}
              aria-label="Fermer le menu de navigation"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </div>
        <div className={styles.scroll}>
          <div className={styles.links}>
            <section className={`${styles.category} ${styles.essential}`} aria-label="Essentiel">
              <div className={styles.categoryLinks}>
                {ESSENTIAL_LINKS.map((item) => (
                  <div key={item.key}>{renderLink(item)}</div>
                ))}
                {currentElseworld && universeSlug && (
                  <div key="current-elseworld">
                    <Link
                      to={getElseworldForumsPath(universeSlug, currentElseworld)}
                      className={`${styles.link} ${styles.linkActive}`}
                      title={currentElseworld}
                    >
                      <span className={styles.icon} aria-hidden="true">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M2 12h20M12 2a15 15 0 0 1 0 20" />
                        </svg>
                      </span>
                      {!effectiveCollapsed && (
                        <span className={styles.label}>
                          {currentElseworldName || currentElseworld}
                        </span>
                      )}
                    </Link>
                  </div>
                )}
              </div>
            </section>

            {hasCommunitySection && (
              <CollapsibleSection
                title="Communauté"
                sectionId="community"
                isOpen={communityOpen}
                onToggle={toggleCommunity}
                effectiveCollapsed={effectiveCollapsed}
                hasUnread={communityHasUnread}
              >
                {itemsByKey.get('rp_activities') && (
                  <div key="rp_activities">{renderLink(itemsByKey.get('rp_activities'))}</div>
                )}
                {showAnimatorDashboard && (
                  <div key="animator_dashboard">{renderLink(ANIMATOR_DASHBOARD_LINK)}</div>
                )}
                <div key="factions">
                  {renderLink(FACTIONS_LINK)}
                </div>
                {itemsByKey.get('member_of_month') && (
                  <div key="member_of_month">{renderLink(itemsByKey.get('member_of_month'))}</div>
                )}
                {itemsByKey.get('character_of_month') && (
                  <div key="character_of_month">{renderLink(itemsByKey.get('character_of_month'))}</div>
                )}
                {forumOrphanItems.map((item) => (
                  <div key={item.key}>{renderLink(item)}</div>
                ))}
              </CollapsibleSection>
            )}

            {guidanceItems.length > 0 && (
              <CollapsibleSection
                title="Ressources"
                sectionId="resources"
                isOpen={resourcesOpen}
                onToggle={toggleResources}
                effectiveCollapsed={effectiveCollapsed}
                hasUnread={false}
              >
                {guidanceItems.map((item) => (
                  <div key={item.key}>{renderLink(item)}</div>
                ))}
              </CollapsibleSection>
            )}

            {user && (
              <CollapsibleSection
                title="Mon compte"
                sectionId="account"
                isOpen={accountOpen}
                onToggle={toggleAccount}
                effectiveCollapsed={effectiveCollapsed}
                hasUnread={false}
              >
                {ACCOUNT_LINKS.map((item) => (
                  <div key={item.key}>{renderLink(item)}</div>
                ))}
              </CollapsibleSection>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default UniverseSidebar;
